/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { RdpvcBridgeMsgType, VCWireMsg } from "./vc-wire-message";
import { AsyncWaitPromise, INFINITE, WaitResultCode } from "./async-event";
import { Logger } from "../../../../core/libs/logger";
import { ElapsedTimer } from "./elapsed-timer";

export const VCSTREAM_DEFAULT = 4294967296 - 1;
export const VCSTREAM_NONE = 4294967296 - 2;
export class VCTransportMsg {
   //extend VCTransport
   private m_vcChannel: any;
   private m_processed: boolean;
   private m_wait: boolean;
   private m_complete: boolean;
   private m_retry: boolean;
   private m_timer: ElapsedTimer;
   private m_hCompletionEvent: AsyncWaitPromise;
   static sMsgID: number = 1;

   public readonly m_msgType: RdpvcBridgeMsgType;
   public m_wireMsg: VCWireMsg;
   public m_pUserData: any;
   public m_processLocally: boolean;

   constructor(msgType: RdpvcBridgeMsgType, wait: boolean, vcChannel: any) {
      this.m_msgType = msgType;
      this.init(wait, vcChannel);
   }
   static FromData(wireMsg: VCWireMsg, wait: boolean, vcChannel: any): VCTransportMsg {
      const newInstances = new VCTransportMsg(RdpvcBridgeMsgType.WireMsgNone, wait, vcChannel);
      newInstances.init(wait, vcChannel);
      newInstances.setMsg(wireMsg);
      return newInstances;
   }
   public init(wait: boolean, vcChannel: any) {
      this.m_vcChannel = vcChannel;
      this.m_wait = wait;
      this.m_complete = false;
      this.m_processed = false;
      this.m_retry = false;
      //this.m_processLocally = m_msgType == WireMsgThunk;//separate it out
      this.m_pUserData = null;
      this.m_hCompletionEvent = new AsyncWaitPromise();

      if (this.m_wait) {
         this.m_hCompletionEvent.create("Transport Message");
      }

      this.m_wireMsg = {
         hdr: {
            msgType: this.m_msgType,
            vcStreamID: VCSTREAM_NONE,
            msgID: VCTransportMsg.sMsgID++
         }
      };
   }
   public setMsg(wireMsg: VCWireMsg) {
      this.m_wireMsg = wireMsg;
   }
   getChannel = () => {
      return this.m_vcChannel;
   };
   wasProcessed = () => {
      return this.m_processed;
   };
   shouldWait = () => {
      return this.m_wait;
   };
   isComplete = () => {
      return this.m_complete;
   };

   msgTypeStr = (): string => {
      return VCTransportMsg.MsgTypeStr(this.m_msgType);
   };
   static MsgTypeStr = (msgType: RdpvcBridgeMsgType): string => {
      switch (msgType) {
         case RdpvcBridgeMsgType.WireMsgNone:
            return "WireMsgNone";
         case RdpvcBridgeMsgType.WireMsgThunk:
            return "WireMsgThunk";
         case RdpvcBridgeMsgType.WireMsgConnect:
            return "WireMsgConnect";
         case RdpvcBridgeMsgType.WireMsgDisconnect:
            return "WireMsgDisconnect";
         case RdpvcBridgeMsgType.WireMsgData:
            return "WireMsgData";
         case RdpvcBridgeMsgType.WireMsgSessionInfoQuery:
            return "WireMsgSessionInfoQuery";
         case RdpvcBridgeMsgType.WireMsgSessionInfoResponse:
            return "WireMsgSessionInfoResponse";
      }
      return "MsgUnknown";
   };
   msgID = (): number => {
      if (!this.m_wireMsg) {
         return null;
      }
      return this.m_wireMsg.hdr.msgID;
   };

   /**
    * This method is called when processing of the message
    * has been completed.
    */
   setComplete = () => {
      this.m_complete = true;
      if (this.m_hCompletionEvent.isValid()) {
         this.m_hCompletionEvent.set();
      }
   };
   /**
    * Waits for the message to be processed.  The transport thread
    * will call SetComplete() when the processing has been completed.
    *
    * @param msTimeout
    */
   waitForCompletion = (msTimeout: number = INFINITE): Promise<boolean> => {
      return new Promise((resolve) => {
         try {
            if (this.m_hCompletionEvent.isntValid()) {
               if (this.m_wait) {
                  Logger.debug(`${this.msgTypeStr()}(${this.msgID()}) failed (invalid event)`, Logger.RDPVCBRIDGE);
                  resolve(true);
               }
            }
            this.m_hCompletionEvent
               .wait(msTimeout)
               .then((rc: WaitResultCode) => {
                  if (rc === WaitResultCode.WAIT_RC_OK) {
                     if (!this.m_complete) {
                        Logger.debug(
                           `${this.msgTypeStr()}(${this.msgID()}) event(${this.m_hCompletionEvent.getDebugID()}) is set, but not complete!!`,
                           Logger.RDPVCBRIDGE
                        );
                     }
                  } else if (rc == WaitResultCode.WAIT_RC_TIMEOUT) {
                     Logger.debug(
                        `${this.msgTypeStr()}(${this.msgID()}) event(${this.m_hCompletionEvent.getDebugID()}) timed out while waiting`,
                        Logger.RDPVCBRIDGE
                     );
                  } else {
                     Logger.error(
                        `${this.msgTypeStr()}(${this.msgID()}) event(${this.m_hCompletionEvent.getDebugID()}) failed while waiting`,
                        Logger.RDPVCBRIDGE
                     );
                  }
                  resolve(rc != WaitResultCode.WAIT_RC_TIMEOUT);
               })
               .catch((e) => {
                  Logger.error("error found in m_hCompletionEvent for waitForCompletion", Logger.RDPVCBRIDGE);
                  resolve(true);
               });
         } catch (e) {
            Logger.error("error found in waitForCompletion", Logger.RDPVCBRIDGE);
            resolve(true);
         }
      });
   };
}
